import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../context/AuthContext";
import { useStreamsState } from "../context/StreamsStateContext";
import { subscribeToStreamDetectionMessages } from "../context/AppContext";

export type StreamDetectionTarget = {
  id: string;
  sensor?: string;
};

export type StreamDetectionDetails = {
  detectionState?: string;
  detectedStreamInfo?: unknown;
};

type StreamDetectionEventPayload = {
  sessionId?: string;
  streamId?: string;
  detectionState?: string;
  detectedStreamInfo?: unknown;
};

type UseStreamDetectionInfoOptions = {
  enabled?: boolean;
};

export const useStreamDetectionInfo = ({ enabled = true }: UseStreamDetectionInfoOptions = {}) => {
  const { authorizedFetch } = useAuth();
  const { getStreamState, streamStates } = useStreamsState();
  const queryClient = useQueryClient();
  const [detectionStream, setDetectionStream] = useState<StreamDetectionTarget | null>(null);

  useEffect(() => {
    if (!detectionStream?.id) {
      return;
    }

    const state = getStreamState(detectionStream.id);
    const normalized = typeof state === "string" ? state.trim().toLowerCase() : "";
    if (!normalized || normalized === "online") {
      return;
    }

    queryClient.setQueryData<StreamDetectionDetails>(["stream-detection", detectionStream.id], (previous) => {
      if (!previous?.detectedStreamInfo) {
        return previous;
      }
      return {
        detectionState: previous.detectionState ?? state,
      };
    });
  }, [detectionStream?.id, getStreamState, queryClient, streamStates]);

  const {
    data: detectionData,
    isFetching: isDetectionLoading,
    isError: isDetectionError,
    error: detectionError
  } = useQuery<StreamDetectionDetails>({
    queryKey: ["stream-detection", detectionStream?.id],
    enabled: enabled && Boolean(detectionStream?.id),
    retry: false,
    queryFn: async ({ queryKey }) => {
      const streamId = queryKey[1];
      if (typeof streamId !== "string" || !streamId) {
        throw new Error("Stream id is required");
      }
      const response = await authorizedFetch(`/api/stream-detections?streamId=${encodeURIComponent(streamId)}`);
      const payload = (await response.json().catch(() => null)) as (StreamDetectionDetails & { error?: string }) | null;
      if (!response.ok) {
        throw new Error(payload?.error ?? "Unable to load stream info");
      }
      return payload ?? {};
    },
    refetchInterval: (query) => {
      const queryData = query.state.data;
      const stateValue = typeof queryData?.detectionState === "string" ? queryData.detectionState.trim().toLowerCase() : "";
      const hasInfo = queryData?.detectedStreamInfo !== undefined && queryData?.detectedStreamInfo !== null;
      return stateValue === "detecting" && !hasInfo ? 2000 : false;
    }
  });

  const { normalizedState, hasDetectionInfo } = useMemo(() => {
    const value = detectionData?.detectionState;
    const normalized = typeof value === "string" ? value.trim() : "";
    const hasInfo = detectionData?.detectedStreamInfo !== undefined && detectionData?.detectedStreamInfo !== null;
    return { normalizedState: normalized, hasDetectionInfo: hasInfo };
  }, [detectionData?.detectionState, detectionData?.detectedStreamInfo]);

  const effectiveDetectionState = useMemo(() => {
    if (hasDetectionInfo && normalizedState.toLowerCase() === "detecting") {
      return "Detected";
    }
    if (!normalizedState && hasDetectionInfo) {
      return "Detected";
    }
    return normalizedState;
  }, [hasDetectionInfo, normalizedState]);

  const detectionStateLabel = effectiveDetectionState || (hasDetectionInfo ? "Detected" : "Unknown");
  const showDetectingSpinner = !hasDetectionInfo && normalizedState.toLowerCase() === "detecting";

  useEffect(() => {
    if (!enabled || !detectionStream?.id) {
      return;
    }

    const streamId = detectionStream.id.trim();
    if (!streamId) {
      return;
    }

    const handleStreamDetectionMessage = (message: { type: string; data: unknown }) => {
      if (message.type !== "streamDetectionUpdated") {
        return;
      }

      const payload = message.data as StreamDetectionEventPayload | null;
      if (!payload) {
        return;
      }

      const payloadStreamId = typeof payload.streamId === "string" ? payload.streamId.trim() : "";
      if (payloadStreamId && payloadStreamId !== streamId) {
        return;
      }

      queryClient.setQueryData<StreamDetectionDetails>(["stream-detection", streamId], (previous) => {
        const normalizedStateRaw =
          typeof payload?.detectionState === "string" ? payload.detectionState.trim() : undefined;
        const normalizedStateLower = normalizedStateRaw?.toLowerCase();
        const nextStateCandidate = normalizedStateRaw ?? previous?.detectionState;

        let infoCandidate: unknown = previous?.detectedStreamInfo;
        if (Object.prototype.hasOwnProperty.call(payload, "detectedStreamInfo")) {
          infoCandidate =
            payload.detectedStreamInfo === null || payload.detectedStreamInfo === undefined
              ? undefined
              : payload.detectedStreamInfo;
        }

        if (normalizedStateLower) {
          const isDetectedState =
            normalizedStateLower === "detected" || normalizedStateLower === "detecting";
          if (!isDetectedState) {
            infoCandidate = undefined;
          }
        }

        const next: StreamDetectionDetails = {
          detectionState: nextStateCandidate
        };
        if (typeof infoCandidate !== "undefined") {
          next.detectedStreamInfo = infoCandidate;
        }
        return next;
      });
    };

    // Subscribe to stream detection messages from AppContext
    const unsubscribe = subscribeToStreamDetectionMessages(handleStreamDetectionMessage);

    return unsubscribe;
  }, [detectionStream?.id, enabled, queryClient]);

  useEffect(() => {
    if (!enabled) {
      setDetectionStream(null);
    }
  }, [enabled]);

  const openDetection = useCallback(
    (stream: StreamDetectionTarget) => {
      if (!enabled) {
        return;
      }
      setDetectionStream(stream);
    },
    [enabled]
  );

  const closeDetection = useCallback(() => {
    setDetectionStream(null);
  }, []);

  const detectionErrorMessage = useMemo(() => {
    if (!isDetectionError) {
      return null;
    }
    if (detectionError instanceof Error) {
      return detectionError.message;
    }
    return "Unable to load stream info";
  }, [detectionError, isDetectionError]);

  return {
    detectionStream,
    detectionData,
    detectionStateLabel,
    showDetectingSpinner,
    isDetectionLoading,
    detectionErrorMessage,
    openDetection,
    closeDetection
  };
};
